import { Icon, Input, Switch } from '@flatfile/baseline'
import React from 'react'
import { FieldTypePanel } from './FieldTypePanel'
import { MentionInput } from './MentionInput'

export interface BaseFieldPanelProps {
  onBack: () => void
  onClose: () => void
  onShowFieldType: () => void
  showFieldType: boolean
  onHideFieldType: () => void
  fieldTypePanelRef: React.RefObject<HTMLDivElement>
  panelRef: React.RefObject<HTMLFormElement>
  title: string
  selectedFieldType: string
  fieldName: string
  onFieldNameChange: (name: string) => void
  onTypeSelect: (type: string) => void
  onSave: () => Promise<void>
  isSaving?: boolean
  saveButtonText: string
  showCancelButton?: boolean
  onCancel?: () => void
  hasChanges?: boolean
  error?: string | null
  onErrorClear?: () => void
  aiAutofill?: boolean
  onAiAutofillChange?: (autofill: boolean) => void
  promptValue?: string
  onPromptChange?: (value: string) => void
  showPromptInput?: boolean
  getPromptPlaceholder?: () => string
  fieldRef?: React.RefObject<HTMLInputElement>
}

export function BaseFieldPanel({
  onBack,
  onClose,
  onShowFieldType,
  showFieldType,
  onHideFieldType,
  fieldTypePanelRef,
  panelRef,
  title,
  selectedFieldType,
  fieldName,
  onFieldNameChange,
  onTypeSelect,
  onSave,
  isSaving = false,
  saveButtonText,
  showCancelButton = false,
  onCancel,
  hasChanges = false,
  error,
  onErrorClear,
  aiAutofill = false,
  onAiAutofillChange,
  promptValue = '',
  onPromptChange,
  showPromptInput = false,
  getPromptPlaceholder,
  fieldRef,
}: BaseFieldPanelProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isSaving && hasChanges && fieldName.trim()) {
      onSave()
    }
  }

  return (
    <div className='relative w-full h-full overflow-hidden'>
      {/* Main Panel */}
      <div
        className={`absolute inset-0 transition-transform duration-300 ${showFieldType ? '-translate-x-full' : 'translate-x-0'}`}
      >
        <form
          ref={panelRef}
          className='px-4 pt-4 pb-0 h-fit-content flex flex-col'
          onSubmit={handleSubmit}
        >
          {/* Header */}
          <div className='flex items-center justify-between mb-4'>
            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={onBack}
                className='text-[#64748b] hover:text-[#1b1b1e]'
              >
                <Icon name='obvious-arrow-left' size={16} />
              </button>
              <span className='text-sm font-medium text-[#1b1b1e]'>
                {title}
              </span>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='w-6 h-6 bg-[#f1f5f9] hover:bg-[#e2e8f0] rounded-full flex items-center justify-center text-[#64748b] hover:text-[#1b1b1e] transition-colors'
            >
              <Icon name='obvious-close-x' size={16} />
            </button>
          </div>

          {/* Field name input */}
          <div className='mb-4'>
            <Input
              type='text'
              placeholder='Field name...'
              value={fieldName}
              onChange={(e) => {
                onFieldNameChange(e.target.value)
                onErrorClear?.()
              }}
              className='flex-1 text-sm px-3 text-[#1b1b1e] placeholder-[#838383]  bg-[#f8f9fa] border-[#e0e1e6] rounded-lg'
              variant='ghost'
              ref={fieldRef}
            />
          </div>

          {/* Type selector */}
          <div className='mb-4'>
            <div className='flex items-center justify-between py-1'>
              <span className='text-sm font-medium text-[#1b1b1e]'>Type</span>
              <button
                type='button'
                onClick={onShowFieldType}
                className='flex items-center gap-2 text-[#64748b] hover:text-[#1b1b1e]'
              >
                <Icon name='obvious-menu' size={16} />
                <span className='text-sm'>{selectedFieldType}</span>
                <Icon name='obvious-chevron-right' size={16} />
              </button>
            </div>
          </div>

          {/* Prompt input for smart field types */}
          {showPromptInput && (
            <div className='mb-4'>
              <div className='mb-2'>
                <span className='text-sm font-medium text-[#1b1b1e]'>
                  Prompt
                </span>
              </div>
              <MentionInput
                value={promptValue}
                onChange={(value) => {
                  onPromptChange?.(value)
                }}
                placeholder={getPromptPlaceholder?.() || 'Enter your prompt...'}
              />

              <div className='flex items-center justify-between py-1'>
                <span className='text-sm font-medium text-[#1b1b1e]'>
                  AI autofill
                </span>
                <Switch
                  checked={aiAutofill}
                  onCheckedChange={onAiAutofillChange}
                />
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className='mb-4 p-2 bg-red-50 border border-red-200 rounded-lg'>
              <span className='text-sm text-red-600'>{error}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className='mt-auto space-y-2'>
            <button
              type='submit'
              disabled={isSaving || !hasChanges || !fieldName.trim()}
              className='w-full py-2 bg-[#0f172a] text-white-opacity-1 text-sm font-medium rounded-lg hover:bg-[#1e293b] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isSaving ? 'Saving...' : saveButtonText}
            </button>
            {showCancelButton && onCancel && (
              <button
                type='button'
                onClick={onCancel}
                disabled={isSaving}
                className='w-full py-2 bg-[#f1f5f9] text-[#64748b] text-sm font-medium rounded-lg hover:bg-[#e2e8f0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Field Type Panel */}
      <div
        className={`absolute inset-0 transition-transform duration-300 ${showFieldType ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <FieldTypePanel
          onBack={onHideFieldType}
          onClose={onClose}
          selectedType={selectedFieldType}
          onSelectType={onTypeSelect}
          fieldTypePanelRef={fieldTypePanelRef}
        />
      </div>
    </div>
  )
}

// Shared utility functions
export const isSmartFieldType = (fieldType: string): boolean => {
  return ['Smart fill', 'Web search', 'Translate'].includes(fieldType)
}

export const getPromptPlaceholder = (fieldType: string): string => {
  switch (fieldType) {
    case 'Smart fill':
      return 'Describe how to fill this field...'
    case 'Web search':
      return 'Enter your search query...'
    case 'Translate':
      return 'Translate @Notes...'
    default:
      return 'Enter your prompt...'
  }
}
